import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { Telemetry } from '@/lib/telemetry'
import type { OutboundClickedProps } from '@/lib/telemetry-types'
import type { CatalogIND } from '@/lib/catalog'

/**
 * Shared card for an IND catalog entry. Used by both the public landing
 * page (commons.chronabio.ai/) and the signed-in portfolio list so the
 * two surfaces stay visually identical and read from the same catalog
 * data (commons/public/catalog.json).
 */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export function formatClearanceDate(iso: string): string {
  const [yyyy, mm, dd] = iso.split('-').map((s) => parseInt(s, 10))
  if (!yyyy || !mm || !dd) return iso
  return `${dd} ${MONTHS[mm - 1]} ${yyyy}`
}

/**
 * One-line metadata strip ("50 submissions · 527 documents · …").
 *
 * Clearance is rendered as a SINGLE item, never two:
 *   - if `ind_clearance_date` is set → `Cleared <date>` (drops the
 *     standalone `ind_status` to avoid the "IND cleared · Cleared <date>"
 *     duplication)
 *   - otherwise → `ind_status` as-is (e.g., "IND cleared", "Pre-IND")
 *
 * `compact` drops the clearance item entirely (the compact card on the
 * signed-in surface omits the date to keep the strip to a single line).
 */
export function buildMetadataLine(
  ind: CatalogIND,
  { compact = false }: { compact?: boolean } = {},
): string {
  const parts: string[] = []
  if (ind.submission_count != null) {
    parts.push(
      `${ind.submission_count} ${ind.submission_count === 1 ? 'submission' : 'submissions'}`,
    )
  }
  if (ind.document_count != null) {
    // Pin locale: the SEO prerender (inject-catalog-plugin.ts) runs in
    // Node with the build host's locale; React re-renders client-side
    // with the user's locale. Any drift produces a visible flash on
    // hydration. Both paths use 'en-US'.
    parts.push(`${ind.document_count.toLocaleString('en-US')} documents`)
  }
  if (!compact) {
    if (ind.ind_clearance_date) {
      parts.push(`Cleared ${formatClearanceDate(ind.ind_clearance_date)}`)
    } else {
      parts.push(ind.ind_status)
    }
  }
  if (ind.includes_pre_ind) {
    parts.push('with Pre-IND correspondence')
  }
  return parts.join(' · ')
}

export interface CatalogCardProps {
  ind: CatalogIND
  /**
   * Optional internal route the card title should link to. When omitted,
   * the title renders as plain text (no link).
   *
   * In the default (non-compact) layout the body has its own outbound
   * links (source, publication), so only the title is the Link. In
   * compact mode the whole card is the click target.
   */
  linkTo?: string
  onLinkClick?: () => void
  /** PostHog `placement` for outbound-link telemetry inside the card. */
  telemetryPlacement?: OutboundClickedProps['placement']
  /**
   * Compact layout used on the signed-in portfolio list:
   *   - hides description, publication, license, and the clearance date
   *   - drops the inline outbound `source` link (rendered as plain text)
   *   - wraps the whole card in the `linkTo` Link with a chevron
   *     affordance that slides on hover, so the click target matches the
   *     hover region
   */
  compact?: boolean
}

export function CatalogCard({
  ind,
  linkTo,
  onLinkClick,
  telemetryPlacement = 'landing_body',
  compact = false,
}: CatalogCardProps) {
  const sponsorLine = ind.sponsor_affiliation
    ? `${ind.sponsor} · ${ind.sponsor_affiliation}`
    : ind.sponsor
  const showCode = !ind.long_name
    .toLowerCase()
    .startsWith(ind.name.toLowerCase())

  const titleInner: ReactNode = (
    <>
      {showCode && (
        <>
          <span>{ind.name}</span>
          <span className="text-slate-400 font-normal"> — </span>
        </>
      )}
      <span className={showCode ? 'text-slate-500 font-medium' : ''}>
        {ind.long_name}
      </span>
    </>
  )

  if (compact) {
    const indTypeBadge = ind.ind_type ? (
      <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600">
        {ind.ind_type}
      </span>
    ) : null

    const body = (
      <>
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-base font-semibold text-brand-charcoal">
            {titleInner}
          </h3>
          <div className="flex items-center gap-2 shrink-0">
            {indTypeBadge}
            {linkTo && (
              <ChevronRight
                aria-hidden
                className="w-4 h-4 text-slate-400 transition-all group-hover:text-brand-orange motion-safe:group-hover:translate-x-0.5"
              />
            )}
          </div>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          {sponsorLine} · {ind.indication} · {ind.modality}
        </p>
        <p className="mt-2 text-xs text-slate-400 uppercase tracking-wider">
          {buildMetadataLine(ind, { compact: true })}
        </p>
        <p className="mt-2 text-xs text-slate-500">{ind.source}</p>
        {/* CC-BY-4.0 §3.a.1 requires the license notice ride with the
            work on every redistribution surface; keep it visible even
            in compact mode so the signed-in tile alone is compliant. */}
        {ind.license && (
          <p className="mt-1 text-[11px] text-slate-400">
            Released under {ind.license}
          </p>
        )}
      </>
    )

    if (linkTo) {
      return (
        <Link
          to={linkTo}
          onClick={onLinkClick}
          aria-label={`Open ${ind.name} portfolio — ${ind.long_name}`}
          className="group block h-full rounded-lg border border-slate-200 bg-white px-5 py-5 transition-all hover:border-brand-orange/60 hover:shadow-sm"
        >
          {body}
        </Link>
      )
    }
    return (
      <article className="h-full rounded-lg border border-slate-200 bg-white px-5 py-5">
        {body}
      </article>
    )
  }

  return (
    <article className="rounded-lg border border-slate-200 bg-white px-5 py-5 transition-all hover:border-brand-orange/50 hover:shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-base font-semibold text-brand-charcoal">
          {linkTo ? (
            <Link
              to={linkTo}
              onClick={onLinkClick}
              className="hover:text-brand-orange transition-colors"
            >
              {titleInner}
            </Link>
          ) : (
            titleInner
          )}
        </h3>
        {ind.ind_type && (
          <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600">
            {ind.ind_type}
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-600">
        {sponsorLine} · {ind.indication} · {ind.modality}
      </p>
      <p className="mt-2 text-xs text-slate-400 uppercase tracking-wider">
        {buildMetadataLine(ind)}
      </p>
      <p className="mt-2 text-xs text-slate-500">
        {ind.source}
        {ind.source_url && (
          <>
            {' · '}
            <a
              href={ind.source_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                Telemetry.outboundClicked({
                  destination: 'ind_source',
                  placement: telemetryPlacement,
                })
              }
              className="text-brand-orange hover:underline"
            >
              source
            </a>
          </>
        )}
      </p>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">
        {ind.description_short}
      </p>
      {ind.publication && (
        <p className="mt-2 text-xs text-slate-500">
          Companion paper:{' '}
          <a
            href={ind.publication.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              Telemetry.outboundClicked({
                destination: 'ind_publication',
                placement: telemetryPlacement,
              })
            }
            className="text-brand-orange hover:underline"
          >
            {ind.publication.title}
          </a>{' '}
          · {ind.publication.venue}
          {ind.publication.doi ? ` · doi:${ind.publication.doi}` : ''}
        </p>
      )}
      {ind.license && (
        <p className="mt-1 text-[11px] text-slate-400">
          Released under {ind.license}
        </p>
      )}
    </article>
  )
}
